import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogPostDetails } from '@/mocks/blog';
import BlueprintCTA from './components/BlueprintCTA';
import { useSEO } from '@/hooks/useSEO';

const BlogDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const post = blogPostDetails.find(p => p.id === id);

  useSEO(
    post
      ? {
          title: `${post.title} | ZpiritualZ Blog`,
          description: post.excerpt,
          canonical: `https://www.zpiritualz.com/blog/${post.id}`,
          ogImage: post.image,
          jsonLd: [
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://www.zpiritualz.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Blog',
                  item: 'https://www.zpiritualz.com/blog',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: post.category,
                  item: `https://www.zpiritualz.com/blog?cat=${encodeURIComponent(post.category)}`,
                },
                {
                  '@type': 'ListItem',
                  position: 4,
                  name: post.title,
                  item: `https://www.zpiritualz.com/blog/${post.id}`,
                },
              ],
            },
            {
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              headline: post.title,
              description: post.excerpt,
              image: post.image,
              url: `https://www.zpiritualz.com/blog/${post.id}`,
              datePublished: post.date,
              author: {
                '@type': 'Organization',
                name: 'ZpiritualZ',
                url: 'https://www.zpiritualz.com',
              },
              publisher: {
                '@type': 'Organization',
                name: 'ZpiritualZ',
                url: 'https://www.zpiritualz.com',
              },
              articleSection: post.category,
              inLanguage: 'nl',
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://www.zpiritualz.com/blog/${post.id}`,
              },
            },
          ],
        }
      : {
          title: 'Blog | ZpiritualZ',
          description: 'Spirituele kennis over bescherming, hing-rituelen en eeuwenoude tradities.',
          canonical: 'https://www.zpiritualz.com/blog',
        }
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#050010] pt-32 flex flex-col items-center justify-center gap-4">
        <i className="ri-file-unknow-line text-violet-400 text-5xl" />
        <p className="text-white text-lg font-semibold">Artikel niet gevonden</p>
        <Link to="/blog" className="px-6 py-3 bg-violet-600/30 border border-violet-500/30 text-violet-300 rounded-xl text-sm hover:bg-violet-600/40 transition-colors cursor-pointer">
          Terug naar blog
        </Link>
      </div>
    );
  }

  const relatedPosts = blogPostDetails.filter(p => post.relatedIds.includes(p.id));

  return (
    <div className="min-h-screen bg-[#050010] pt-20">
      {/* ── HERO ─────────────────────────────────────────────────── */}
      <div className="relative h-[420px] md:h-[540px] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050010]/50 via-[#050010]/40 to-[#050010]" />
        <div className="absolute inset-0 flex flex-col justify-end pb-10 px-6 max-w-[900px] mx-auto w-full left-0 right-0">
          {/* Breadcrumb nav — zichtbaar + matched met Schema.org markup */}
          <nav aria-label="Breadcrumb" className="mb-4">
            <ol className="flex items-center gap-1.5 flex-wrap">
              <li>
                <Link to="/" className="text-white/35 text-xs hover:text-violet-300 transition-colors cursor-pointer whitespace-nowrap">
                  Home
                </Link>
              </li>
              <li className="text-white/20 text-xs">
                <i className="ri-arrow-right-s-line" />
              </li>
              <li>
                <Link to="/blog" className="text-white/35 text-xs hover:text-violet-300 transition-colors cursor-pointer whitespace-nowrap">
                  Blog
                </Link>
              </li>
              <li className="text-white/20 text-xs">
                <i className="ri-arrow-right-s-line" />
              </li>
              <li>
                <Link
                  to={`/blog?cat=${encodeURIComponent(post.category)}`}
                  className="text-white/35 text-xs hover:text-violet-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {post.category}
                </Link>
              </li>
              <li className="text-white/20 text-xs">
                <i className="ri-arrow-right-s-line" />
              </li>
              <li>
                <span className="text-violet-300 text-xs line-clamp-1 max-w-[200px] sm:max-w-xs">
                  {post.title}
                </span>
              </li>
            </ol>
          </nav>
          <span className="inline-block px-3 py-1 bg-violet-600/30 border border-violet-500/30 text-violet-300 text-xs rounded-full mb-3 w-fit">
            {post.category}
          </span>
          <h1 className="text-2xl md:text-4xl font-black text-white leading-tight mb-3 max-w-3xl">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-white/40 text-sm flex-wrap">
            <span className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full overflow-hidden shrink-0">
                <img src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover" />
              </div>
              {post.author}
            </span>
            <span><i className="ri-calendar-line mr-1" />{post.date}</span>
            <span><i className="ri-time-line mr-1" />{post.readTime} leestijd</span>
          </div>
        </div>
      </div>

      {/* ── CONTENT ──────────────────────────────────────────────── */}
      <div className="max-w-[900px] mx-auto px-6 py-12">
        {/* Excerpt intro */}
        <p className="text-white/70 text-lg leading-relaxed border-l-2 border-violet-500/50 pl-5 mb-10 italic">
          {post.excerpt}
        </p>

        {/* Preview paragraphs */}
        <div className="space-y-6 mb-4">
          {post.previewParagraphs.map((para, idx) => (
            <p key={idx} className="text-white/65 text-base leading-relaxed">
              {para}
            </p>
          ))}
        </div>

        {/* Locked hint strip */}
        <div className="flex items-start gap-3 p-5 bg-violet-600/10 border border-violet-500/20 rounded-2xl mb-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-600/25 shrink-0">
            <i className="ri-lock-2-line text-violet-400 text-base" />
          </div>
          <div>
            <p className="text-violet-300 font-semibold text-sm mb-1">Dit is de introductie</p>
            <p className="text-white/50 text-sm leading-relaxed">{post.lockedHint}</p>
          </div>
        </div>

        {/* Blueprint purchase */}
        <BlueprintCTA id={post.id} title={post.blueprintTitle} includes={post.blueprintIncludes} />

        {/* Guarantee strip */}
        <div className="flex flex-col sm:flex-row items-center gap-4 p-5 bg-white/3 border border-white/8 rounded-2xl mb-12">
          {[
            { icon: 'ri-file-download-line', text: 'Directe e-mailbezorging na betaling' },
            { icon: 'ri-shield-check-line', text: '100% veilig betalen via Stripe' },
            { icon: 'ri-customer-service-2-line', text: 'Vragen? Neem contact op' },
          ].map(item => (
            <div key={item.text} className="flex items-center gap-2 text-white/40 text-sm flex-1 justify-center">
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-violet-400 text-base`} />
              </div>
              {item.text}
            </div>
          ))}
        </div>

        {/* Gerelateerde artikelen */}
        {relatedPosts.length > 0 && (
          <div>
            <h3 className="text-white font-bold text-xl mb-6">Gerelateerde artikelen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {relatedPosts.map(rp => (
                <div
                  key={rp.id}
                  onClick={() => navigate(`/blog/${rp.id}`)}
                  className="group bg-white/3 border border-violet-500/10 rounded-2xl overflow-hidden hover:border-violet-500/30 transition-all cursor-pointer"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img src={rp.image} alt={rp.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050010]/70 via-transparent to-transparent" />
                    <span className="absolute top-3 left-3 px-2 py-1 bg-black/50 backdrop-blur-sm text-violet-300 text-xs rounded-full">{rp.category}</span>
                  </div>
                  <div className="p-4">
                    <h4 className="text-white font-semibold text-sm leading-snug mb-2 group-hover:text-violet-400 transition-colors line-clamp-2">{rp.title}</h4>
                    <div className="flex items-center justify-between">
                      <span className="text-white/30 text-xs"><i className="ri-time-line mr-1" />{rp.readTime} leestijd</span>
                      <span className="text-violet-400 text-xs flex items-center gap-1">Lees meer <i className="ri-arrow-right-line" /></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogDetailPage;
